import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import './EspaceStats.css';

const EspaceStats = () => {
  const token = localStorage.getItem('token');

  // Filters State
  const [localisation, setLocalisation] = useState("All");
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  // Data States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [bills, setBills] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Helper function to extract location ID robustly
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) {
      if (typeof loc._id === 'string') return loc._id;
      if (loc._id.$oid) return loc._id.$oid;
      return loc._id.toString();
    }
    return loc.toString();
  };

  // Fetch all required data
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError("");
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const configAuth = { headers: { Authorised: token } };

      const [locationsRes, productsRes, billsRes, invoicesRes] = await Promise.all([
        axios.get("/api/Locations/getAllLocations"),
        axios.get("/api/ProductV/getProductV?slim=true", configAuth),
        axios.get(`/api/BillsValises/getAllBillsV?slim=true&startDate=${startDate}&endDate=${endDate}`, configAuth),
        axios.get("/api/InvoicesV/getInvoices?slim=true")
      ]);

      setLocations(locationsRes.data || []);
      setProducts(productsRes.data || []);
      setBills(billsRes.data || []);
      setInvoices(invoicesRes.data || []);
    } catch (err) {
      console.error("Error fetching E-space stats data:", err);
      setError("Une erreur est survenue lors du chargement des données. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 1. Identify E-space Locations (containing espace or e-space)
  const espLocations = useMemo(() => {
    return locations.filter(loc => {
      const nameLower = (loc.name || "").toLowerCase();
      return nameLower.includes("espace") || nameLower.includes("e-space");
    });
  }, [locations]);

  // Set of E-space Location IDs
  const espLocationIds = useMemo(() => {
    return new Set(espLocations.map(loc => extractLocId(loc._id)));
  }, [espLocations]);

  // Map of Location ID to Location Name
  const espLocationsMap = useMemo(() => {
    const map = new Map();
    locations.forEach(loc => {
      const id = extractLocId(loc._id);
      if (id) map.set(id, loc.name);
    });
    return map;
  }, [locations]);

  // 2. Pre-calculate Product Categories & Parents (PLAYSTATION / CANDY STORE)
  const productMap = useMemo(() => {
    const map = new Map();
    products.forEach(p => {
      map.set(`prod_${p._id}`, { ...p, isSub: false });
      if (p.subProductsV && Array.isArray(p.subProductsV)) {
        p.subProductsV.forEach(sub => {
          map.set(`sub_${sub._id}`, { 
            ...sub, 
            parentName: p.name, 
            parentId: p._id, 
            parentCategory: p.category, 
            isSub: true 
          });
        });
      }
    });
    return map;
  }, [products]);

  // 3. Pre-calculate Purchase Prices (TTC) from purchase invoices
  const purchasePriceMap = useMemo(() => {
    const map = new Map();
    invoices.forEach(inv => {
      if (!inv.items || !Array.isArray(inv.items)) return;
      inv.items.forEach(item => {
        let purchasePriceTTC = 0;
        if (item.pricettc && item.pricettc > 0) {
          purchasePriceTTC = item.pricettc;
        } else {
          const purchasePriceHT = item.price || 0;
          const tvaRate = item.tva || 0;
          purchasePriceTTC = purchasePriceHT * (1 + tvaRate / 100);
        }

        const itemProductId = item.productId?.toString() || item.productV?.toString() || item.productV?._id?.toString();
        const itemSubProductId = item.subProductId?.toString() || item.subProductV?.toString() || item.subProductV?._id?.toString();

        if (itemSubProductId) {
          map.set(`sub_${itemSubProductId}`, purchasePriceTTC);
        }
        if (itemProductId && !map.has(`prod_${itemProductId}`)) {
          map.set(`prod_${itemProductId}`, purchasePriceTTC);
        }
      });
    });
    return map;
  }, [invoices]);

  const findPurchasePrice = useCallback((productId, subProductId) => {
    if (subProductId && purchasePriceMap.has(`sub_${subProductId}`)) {
      return purchasePriceMap.get(`sub_${subProductId}`);
    }
    if (productId && purchasePriceMap.has(`prod_${productId}`)) {
      return purchasePriceMap.get(`prod_${productId}`);
    }
    return 0;
  }, [purchasePriceMap]);

  // 4. Filter Sales Bills for E-space (by location or user)
  const filteredSalesBills = useMemo(() => {
    return bills.filter(bill => {
      if (bill.status === "cancelled") return false;

      // Extract location ID
      const locId = extractLocId(bill.localisation);

      // Check if this is an E-space bill (either by location, user, or role)
      let isEspBill = false;
      if (locId && espLocationIds.has(locId)) {
        isEspBill = true;
      } else if (bill.user) {
        const role = (bill.user.role || "").toLowerCase();
        const name = (bill.user.name || "").toLowerCase();
        if (role === "userespace" || name.includes("espace") || name.includes("e-space")) {
          isEspBill = true;
        }
      }

      // Check Location filter
      if (localisation !== "All") {
        return isEspBill && locId === localisation;
      }

      return isEspBill;
    });
  }, [bills, espLocationIds, localisation]);

  // 5. Aggregate Sales Metrics, hourly breakdown & product rankings
  const processedMetrics = useMemo(() => {
    let totalCA = 0;
    let totalCost = 0;
    let totalItemsSold = 0;
    const dailySalesMap = {};
    
    // Hourly distribution init (0h to 23h)
    const hourlySalesMap = Array(24).fill(0).map((_, i) => ({
      hourNum: i,
      hourLabel: `${i}h`,
      sales: 0,
      count: 0
    }));

    // Daypart breakdown
    const dayparts = {
      Matin: { name: "Matin (08h-12h)", sales: 0, count: 0 },
      ApresMidi: { name: "Après-Midi (12h-18h)", sales: 0, count: 0 },
      Soiree: { name: "Soirée (18h-23h)", sales: 0, count: 0 },
      Nuit: { name: "Nuit (23h-08h)", sales: 0, count: 0 }
    };

    const categoryMap = {
      PLAYSTATION: { name: "PLAYSTATION (Jeux & Sessions)", quantity: 0, sales: 0, cost: 0 },
      "CANDY STORE": { name: "CANDY STORE (Friandises)", quantity: 0, sales: 0, cost: 0 },
      Autre: { name: "Autre / Divers", quantity: 0, sales: 0, cost: 0 }
    };

    const productSalesMap = {};
    let lastSaleDate = null;

    filteredSalesBills.forEach(bill => {
      const billDate = new Date(bill.date || bill.createdAt);
      if (billDate && (!lastSaleDate || billDate > new Date(lastSaleDate))) {
        lastSaleDate = billDate;
      }

      const dateKey = moment(billDate).format("YYYY-MM-DD");
      const hour = billDate.getHours();
      let billSalesAmount = 0;
      let billCostAmount = 0;

      bill.items?.forEach(item => {
        if (item.isCancelled) return;
        const quantity = item.quantity || 1;
        const sellingPrice = item.price || 0;
        const salesAmount = sellingPrice * quantity;

        // Resolve Product details
        const itemId = item.id?.toString() || item._id?.toString();
        const prodMatch = productMap.get(`prod_${itemId}`);
        const subMatch = productMap.get(`sub_${itemId}`);

        let category = "Autre";
        let purchasePrice = 0;
        let pName = item.name || "Produit inconnu";

        if (prodMatch && !prodMatch.isSub) {
          category = prodMatch.category || "Autre";
          pName = prodMatch.name;
          purchasePrice = findPurchasePrice(prodMatch._id, null);
        } else if (subMatch && subMatch.isSub) {
          category = subMatch.parentCategory || "Autre";
          pName = `${subMatch.parentName} - ${subMatch.name}`;
          purchasePrice = findPurchasePrice(subMatch.parentId, subMatch._id);
        }

        // Map Category stats
        const catKey = (category === "PLAYSTATION" || category === "CANDY STORE") ? category : "Autre";
        
        const costAmount = purchasePrice * quantity;
        totalCA += salesAmount;
        totalCost += costAmount;
        totalItemsSold += quantity;
        billSalesAmount += salesAmount;
        billCostAmount += costAmount;

        categoryMap[catKey].quantity += quantity;
        categoryMap[catKey].sales += salesAmount;
        categoryMap[catKey].cost += costAmount;

        // Map Product sales stats
        if (!productSalesMap[itemId]) {
          productSalesMap[itemId] = { name: pName, category: catKey, quantity: 0, sales: 0, cost: 0 };
        }
        productSalesMap[itemId].quantity += quantity;
        productSalesMap[itemId].sales += salesAmount;
        productSalesMap[itemId].cost += costAmount;
      });

      // Apply bill discount if any to CA calculations
      const discount = bill.discount || 0;
      const netBillSales = billSalesAmount * (1 - discount / 100);

      // Daily trend
      dailySalesMap[dateKey] = (dailySalesMap[dateKey] || 0) + netBillSales;

      // Hourly stats
      if (hour >= 0 && hour < 24) {
        hourlySalesMap[hour].sales += netBillSales;
        hourlySalesMap[hour].count += 1;

        // Classify daypart
        if (hour >= 8 && hour < 12) {
          dayparts.Matin.sales += netBillSales;
          dayparts.Matin.count += 1;
        } else if (hour >= 12 && hour < 18) {
          dayparts.ApresMidi.sales += netBillSales;
          dayparts.ApresMidi.count += 1;
        } else if (hour >= 18 && hour < 23) {
          dayparts.Soiree.sales += netBillSales;
          dayparts.Soiree.count += 1;
        } else {
          dayparts.Nuit.sales += netBillSales;
          dayparts.Nuit.count += 1;
        }
      }
    });

    // Determine Best Sales Day
    let bestDayDate = "N/A";
    let bestDayAmount = 0;
    Object.entries(dailySalesMap).forEach(([date, amt]) => {
      if (amt > bestDayAmount) {
        bestDayAmount = amt;
        bestDayDate = date;
      }
    });

    // Determine Peak Hour
    let peakHour = "N/A";
    let peakHourAmount = 0;
    hourlySalesMap.forEach(h => {
      if (h.sales > peakHourAmount) {
        peakHourAmount = h.sales;
        peakHour = `${h.hourNum}h - ${h.hourNum + 1}h`;
      }
    });

    // Split rankings
    const allProductsRanked = Object.values(productSalesMap).sort((a, b) => b.quantity - a.quantity);
    const playstationRankings = allProductsRanked.filter(p => p.category === "PLAYSTATION");
    const candyRankings = allProductsRanked.filter(p => p.category === "CANDY STORE");

    const topSellingProductGlobal = allProductsRanked[0] || null;
    const topGamePopular = playstationRankings[0] || null;
    const topCandyPopular = candyRankings[0] || null;

    return {
      totalCA,
      totalCost,
      totalMargin: totalCA - totalCost,
      marginPercentage: totalCA > 0 ? ((totalCA - totalCost) / totalCA) * 100 : 0,
      totalItemsSold,
      averageBasket: filteredSalesBills.length > 0 ? totalCA / filteredSalesBills.length : 0,
      dailySalesMap,
      hourlySalesMap,
      dayparts,
      categoryMap,
      bestDay: { date: bestDayDate, amount: bestDayAmount },
      peakHour: { label: peakHour, amount: peakHourAmount },
      lastSaleDate,
      playstationRankings,
      candyRankings,
      topSellingProductGlobal,
      topGamePopular,
      topCandyPopular
    };
  }, [filteredSalesBills, productMap, findPurchasePrice]);

  // Hourly Chart Data (filtering out extreme hours with 0 sales for clear view)
  const hourlyChartData = useMemo(() => {
    return processedMetrics.hourlySalesMap.filter(h => h.hourNum >= 8 || h.sales > 0);
  }, [processedMetrics.hourlySalesMap]);

  // Categories Distribution (PieChart)
  const categoriesPieChartData = useMemo(() => {
    const data = [];
    let idCounter = 0;
    const colors = ['#8b5cf6', '#d946ef', '#6366f1'];

    Object.entries(processedMetrics.categoryMap).forEach(([cat, stats]) => {
      if (stats.sales > 0) {
        const pct = processedMetrics.totalCA > 0 ? ((stats.sales / processedMetrics.totalCA) * 100).toFixed(1) + "%" : "0%";
        data.push({
          id: idCounter++,
          value: stats.sales,
          label: `${cat} (${pct})`,
          color: colors[idCounter % colors.length]
        });
      }
    });

    return data;
  }, [processedMetrics.categoryMap, processedMetrics.totalCA]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value || 0);
  };

  return (
    <div className="espace-dashboard-container">
      <div className="espace-header">
        <h1>Statistiques E-space</h1>
        <p>Analyse des sessions de jeux PlayStation, pics horaires et ventes du Candy Store</p>
      </div>

      {/* Filtres */}
      <div className="espace-filters">
        <div className="espace-filter-group">
          <label>Boutique / Localisation</label>
          <select value={localisation} onChange={(e) => setLocalisation(e.target.value)}>
            <option value="All">Toutes les zones E-space</option>
            {espLocations.map(loc => (
              <option key={loc._id} value={extractLocId(loc._id)}>{loc.name}</option>
            ))}
          </select>
        </div>
        <div className="espace-filter-group">
          <label>Date de début</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="espace-filter-group">
          <label>Date de fin</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#7c3aed', fontWeight: 'bold' }}>Chargement des données E-space en cours...</p>}
      {error && <p style={{ textAlign: 'center', color: '#dc2626', fontWeight: 'bold' }}>{error}</p>}

      {/* KPIs Section */}
      <div className="espace-kpis">
        <div className="espace-kpi-card">
          <div className="espace-kpi-title">Chiffre d'Affaires</div>
          <div className="espace-kpi-value primary">{formatCurrency(processedMetrics.totalCA)}</div>
          <small>Articles & Sessions: {processedMetrics.totalItemsSold}</small>
        </div>
        <div className="espace-kpi-card">
          <div className="espace-kpi-title">Marge Brute</div>
          <div className={`espace-kpi-value ${processedMetrics.totalMargin >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(processedMetrics.totalMargin)}
          </div>
          <small style={{ fontWeight: 'bold', color: processedMetrics.totalMargin >= 0 ? '#16a34a' : '#dc2626' }}>
            Taux: {processedMetrics.marginPercentage.toFixed(1)}%
          </small>
        </div>
        <div className="espace-kpi-card">
          <div className="espace-kpi-title">Heure de Pointe</div>
          <div className="espace-kpi-value warning" style={{ fontSize: '1.7rem', padding: '5px 0' }}>
            {processedMetrics.peakHour.label}
          </div>
          <small style={{ color: '#d97706', fontWeight: 'bold' }}>
            CA cumulé: {formatCurrency(processedMetrics.peakHour.amount)}
          </small>
        </div>
        <div className="espace-kpi-card">
          <div className="espace-kpi-title">Meilleur Jeu / Session</div>
          <div className="espace-kpi-value info" style={{ fontSize: '1.15rem', padding: '10px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={processedMetrics.topGamePopular?.name || "Aucun"}>
            {processedMetrics.topGamePopular ? processedMetrics.topGamePopular.name : "Aucun"}
          </div>
          <small>Vendus: {processedMetrics.topGamePopular?.quantity || 0} fois</small>
        </div>
        <div className="espace-kpi-card">
          <div className="espace-kpi-title">Dernière vente</div>
          <div className="espace-kpi-value primary" style={{ fontSize: '1.4rem', padding: '10px 0', color: '#6d28d9' }}>
            {processedMetrics.lastSaleDate ? moment(processedMetrics.lastSaleDate).format("DD/MM/YYYY HH:mm") : "N/A"}
          </div>
          <small style={{ color: '#5b21b6', fontWeight: 'bold' }}>
            {processedMetrics.lastSaleDate ? moment(processedMetrics.lastSaleDate).fromNow() : "Aucune vente"}
          </small>
        </div>
      </div>

      {/* Graphiques Section */}
      <div className="espace-charts">
        {/* Pics des Heures de vente */}
        <div className="espace-chart-container" style={{ flex: 2 }}>
          <div className="espace-chart-title">📊 Étude des Pics des Heures de Vente (CA par heure)</div>
          {hourlyChartData.length > 0 ? (
            <BarChart
              dataset={hourlyChartData}
              xAxis={[{ scaleType: 'band', dataKey: 'hourLabel' }]}
              series={[{ dataKey: 'sales', color: '#8b5cf6', label: 'Ventes (TND)' }]}
              width={700}
              height={260}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#64748b' }}>Aucune donnée horaire pour cette période.</p>
          )}
          
          <div className="peak-hours-grid-info">
            <div className="peak-hour-box">
              <div className="box-title">🌅 Matinée (8h - 12h)</div>
              <div className="box-value" style={{ color: '#d97706' }}>{formatCurrency(processedMetrics.dayparts.Matin.sales)}</div>
            </div>
            <div className="peak-hour-box">
              <div className="box-title">☀️ Après-Midi (12h - 18h)</div>
              <div className="box-value" style={{ color: '#0284c7' }}>{formatCurrency(processedMetrics.dayparts.ApresMidi.sales)}</div>
            </div>
            <div className="peak-hour-box">
              <div className="box-title">🌆 Soirée (18h - 23h)</div>
              <div className="box-value" style={{ color: '#7c3aed' }}>{formatCurrency(processedMetrics.dayparts.Soiree.sales)}</div>
            </div>
            <div className="peak-hour-box">
              <div className="box-title">🌌 Nuit (23h - 8h)</div>
              <div className="box-value" style={{ color: '#475569' }}>{formatCurrency(processedMetrics.dayparts.Nuit.sales)}</div>
            </div>
          </div>
        </div>

        {/* Répartition Catégories */}
        <div className="espace-chart-container">
          <div className="espace-chart-title">🍕 Ventes par Secteur E-space</div>
          {categoriesPieChartData.length > 0 ? (
            <PieChart
              series={[{
                data: categoriesPieChartData,
                innerRadius: 30,
                outerRadius: 80,
                paddingAngle: 5,
                cornerRadius: 6,
              }]}
              width={380}
              height={260}
              slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' }, labelStyle: { fill: '#374151', fontSize: 12 } } }}
            />
          ) : (
            <p style={{ margin: 'auto', color: '#64748b' }}>Aucune vente enregistrée.</p>
          )}
        </div>
      </div>

      {/* Synthèse par Catégorie */}
      <div className="espace-table-container">
        <h3>📦 Synthèse de l'activité E-space par type</h3>
        <div className="table-responsive">
          <table className="espace-table">
            <thead>
              <tr>
                <th>Type de Produit / Secteur</th>
                <th>Volume Vendu</th>
                <th>CA Réel (Ventes)</th>
                <th>Coût d'Achat</th>
                <th style={{ color: '#7c3aed' }}>Marge Brute</th>
                <th>Taux de Marge</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(processedMetrics.categoryMap).map(([cat, stats]) => {
                const margin = stats.sales - stats.cost;
                const marginPct = stats.sales > 0 ? (margin / stats.sales) * 100 : 0;
                return (
                  <tr key={cat}>
                    <td style={{ fontWeight: 'bold', color: '#111827' }}>{stats.name}</td>
                    <td style={{ fontWeight: '700', color: '#374151' }}>{stats.quantity} pcs</td>
                    <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(stats.sales)}</td>
                    <td style={{ color: '#c2410c', fontWeight: '700' }}>{formatCurrency(stats.cost)}</td>
                    <td style={{ fontWeight: 'bold', color: margin >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(margin)}</td>
                    <td style={{ fontWeight: 'bold', color: '#b45309' }}>{marginPct.toFixed(1)}%</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid rgba(139, 92, 246, 0.3)', fontWeight: 'bold' }}>
                <td style={{ color: '#111827' }}>TOTAL E-SPACE CUMULÉ</td>
                <td style={{ color: '#111827', fontWeight: '800' }}>{processedMetrics.totalItemsSold} pcs</td>
                <td style={{ color: '#111827', fontWeight: '800' }}>{formatCurrency(processedMetrics.totalCA)}</td>
                <td style={{ color: '#c2410c', fontWeight: '800' }}>{formatCurrency(processedMetrics.totalCost)}</td>
                <td style={{ color: processedMetrics.totalMargin >= 0 ? '#15803d' : '#b91c1c', fontWeight: '800' }}>{formatCurrency(processedMetrics.totalMargin)}</td>
                <td style={{ color: '#b45309', fontWeight: '800' }}>{processedMetrics.marginPercentage.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Grid pour les classements */}
      <div className="espace-category-rankings-section">
        <h3 className="section-title">🏆 Classement détaillé des Ventes E-space</h3>
        <div className="espace-category-rankings-grid">
          
          {/* Classement PlayStation Jeux et Sessions */}
          <div className="espace-table-container ranking-card">
            <h4 className="espace-ranking-card-title">
              <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>🎮</span>
              Jeux & Sessions PlayStation
            </h4>
            <div className="table-responsive espace-ranking-table-wrapper">
              <table className="espace-table compact">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Jeu / Session</th>
                    <th style={{ textAlign: 'center' }}>Qté</th>
                    <th>CA Réel</th>
                    <th>Marge</th>
                    <th>% Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {processedMetrics.playstationRankings.map((p, index) => {
                    const margin = p.sales - p.cost;
                    const marginPct = p.sales > 0 ? (margin / p.sales) * 100 : 0;
                    return (
                      <tr key={index}>
                        <td style={{ fontWeight: 'bold', color: '#7c3aed' }}>#{index + 1}</td>
                        <td style={{ fontWeight: '600', color: '#111827' }}>{p.name}</td>
                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>{p.quantity}</td>
                        <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                        <td style={{ fontWeight: 'bold', color: margin >= 0 ? '#15803d' : '#b91c1c' }}>
                          {formatCurrency(margin)}
                        </td>
                        <td style={{ fontWeight: 'bold', color: '#b45309' }}>
                          {marginPct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                  {processedMetrics.playstationRankings.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', color: '#64748b', padding: '40px 10px', fontStyle: 'italic' }}>
                        Aucune session de jeu PlayStation sur cette période.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Classement Candy Store */}
          <div className="espace-table-container ranking-card">
            <h4 className="espace-ranking-card-title">
              <span style={{ marginRight: '8px', fontSize: '1.4rem' }}>🍬</span>
              Ventes Candy Store
            </h4>
            <div className="table-responsive espace-ranking-table-wrapper">
              <table className="espace-table compact">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Produit Candy</th>
                    <th style={{ textAlign: 'center' }}>Qté</th>
                    <th>CA Réel</th>
                    <th>Marge</th>
                    <th>% Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {processedMetrics.candyRankings.map((p, index) => {
                    const margin = p.sales - p.cost;
                    const marginPct = p.sales > 0 ? (margin / p.sales) * 100 : 0;
                    return (
                      <tr key={index}>
                        <td style={{ fontWeight: 'bold', color: '#4f46e5' }}>#{index + 1}</td>
                        <td style={{ fontWeight: '600', color: '#111827' }}>{p.name}</td>
                        <td style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>{p.quantity}</td>
                        <td style={{ fontWeight: '700', color: '#111827' }}>{formatCurrency(p.sales)}</td>
                        <td style={{ fontWeight: 'bold', color: margin >= 0 ? '#15803d' : '#b91c1c' }}>
                          {formatCurrency(margin)}
                        </td>
                        <td style={{ fontWeight: 'bold', color: '#b45309' }}>
                          {marginPct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                  {processedMetrics.candyRankings.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', color: '#64748b', padding: '40px 10px', fontStyle: 'italic' }}>
                        Aucun article Candy Store vendu sur cette période.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default EspaceStats;
